import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type {
  ResourceBuiltinLink,
  ResourceDescriptor,
  ResourceDetailResponse,
  ResourceListResponse,
  ScopeResponse,
} from "@k8s-dashboard/contracts";
import {
  useResourceCatalog,
  useResourceList,
  useResourceMap,
  useResourceObject,
  useScope,
} from "@/api/queries";
import { useScopeAuthGeneration } from "@/api/ScopeAuthLifecycle";
import { useDashboardParams } from "@/state/useDashboardParams";
import { Combobox } from "@/components/Combobox";
import { Panel } from "@/components/primitives";
import { LoadingState } from "@/components/SectionState";
import { VirtualTable } from "@/components/VirtualTable";
import { useSlashFocus } from "@/app/useSlashFocus";
import {
  activatePreferencePartition,
  FAVORITES_MAX_ITEMS,
  readFavorites,
  readTablePreference,
  resumePreferencePartition,
  watchPreferencePartition,
  writeFavorites,
  writeTablePreference,
  type PreferenceScope,
} from "@/app/preference-storage";
import { useT } from "@/i18n/I18nProvider";
import { rememberRecent, type RecentRef } from "@/app/recent";
import { ResourceDetailDrawer } from "./ResourceDetailDrawer";
import {
  builtinRelationFocusKey,
  builtinRelationRequest,
  isExactDetailFocus,
  selectBuiltinRelations,
  type BuiltinRelationRequest,
  type BuiltinRelationsView,
} from "./builtin-relations";
import { explorerState, requestErrorMessage, stateFromError, stateNotice, type StateNotice } from "./state";
import {
  buildExplorerColumns,
  dedupeRows,
  MAX_RETAINED_PAGES,
  MAX_SELECTION,
  PAGE_SIZE,
  sanitizeColumnSettings,
  scopeAllowsNamespace,
  scopeFingerprint,
  selectionIdentity,
  TABLE_HEIGHT,
  TABLE_OVERSCAN,
  TABLE_ROW_HEIGHT,
  type ExplorerColumn,
} from "./resource-list-state";

/* 서버가 강제하는 상한을 UI도 그대로 씁니다 — 더 큰 값을 보내도 서버가 400으로 거절합니다. */
const MAX_NAME_FILTER = 253;
const MAX_LABEL_SELECTOR = 512;
const COLUMN_RESIZE_STEP = 8;
const EMPTY_PAGES: ResourceListResponse[] = [];
const EMPTY_COLUMNS: string[] = [];

type ActiveBuiltinRelationQuery = Readonly<{
  focusKey: string;
  scope: ScopeResponse;
}>;
type BuiltinRelationQueryResult = Readonly<{
  query: ActiveBuiltinRelationQuery;
  relations: BuiltinRelationsView;
}>;

function descriptorKey(d: { group: string; version: string; resource: string }) {
  return `${d.group}/${d.version}/${d.resource}`;
}

function parseKey(value: string): { group: string; version: string; resource: string } | null {
  const parts = value.split("/");
  if (parts.length !== 3 || parts.some((p) => !p)) return null;
  return { group: parts[0], version: parts[1], resource: parts[2] };
}

const STATE_LABEL: Record<string, string> = {
  ready: "조회 가능",
  syncing: "동기화 중",
  unsupported: "미지원",
  forbidden: "권한 없음",
  missing: "미제공",
};

function StateBlock({ notice }: { notice: StateNotice }) {
  const cls = notice.tone === "neutral" ? "state" : `state state--${notice.tone}`;
  return (
    <div className={cls} role={notice.tone === "forbidden" || notice.tone === "error" ? "alert" : undefined} data-state={notice.state}>
      <span className="state__glyph" aria-hidden="true">
        {notice.tone === "neutral" ? "✓" : notice.tone === "forbidden" ? "!" : "▲"}
      </span>
      <span className="state__title">{notice.title}</span>
      <span className="state__detail">{notice.detail}</span>
    </div>
  );
}

function ColumnResizeHandle({ column, width, label, onWidth, onCommit }: {
  column: ExplorerColumn;
  width: number;
  label: string;
  onWidth: (width: number) => void;
  onCommit: (width: number) => void;
}) {
  const clamp = (value: number) => Math.min(column.max, Math.max(column.min, Math.round(value)));
  const onPointerDown = (event: React.PointerEvent<HTMLSpanElement>) => {
    event.preventDefault();
    if (!Number.isFinite(event.clientX)) return;
    const startX = event.clientX;
    const startWidth = width;
    let latest = startWidth;
    const move = (next: PointerEvent) => {
      if (!Number.isFinite(next.clientX)) return;
      latest = clamp(startWidth + next.clientX - startX);
      onWidth(latest);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      onCommit(latest);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up, { once: true });
  };
  return (
    <span
      className="resource-column__resizer"
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuemin={column.min}
      aria-valuemax={column.max}
      aria-valuenow={width}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={(event) => {
        let next: number | undefined;
        if (event.key === "ArrowLeft") next = width - COLUMN_RESIZE_STEP;
        else if (event.key === "ArrowRight") next = width + COLUMN_RESIZE_STEP;
        else if (event.key === "Home") next = column.min;
        else if (event.key === "End") next = column.max;
        if (next === undefined) return;
        event.preventDefault();
        const bounded = clamp(next);
        onWidth(bounded);
        onCommit(bounded);
      }}
    />
  );
}

const BuiltinRelationQueryBridge = memo(function BuiltinRelationQueryBridge({
  detail,
  relationRequest,
  query,
  onRelations,
}: {
  detail: ResourceDetailResponse;
  relationRequest: BuiltinRelationRequest;
  query: ActiveBuiltinRelationQuery;
  onRelations: (result: BuiltinRelationQueryResult) => void;
}) {
  const resourceMap = useResourceMap(
    {
      clusterId: detail.clusterId,
      group: detail.group,
      version: detail.version,
      resource: detail.resource,
      namespace: detail.namespace ?? "",
      name: detail.name,
      uid: detail.uid,
      depth: 1,
      relations: relationRequest.relations,
    },
    true,
  );
  const relations = useMemo(
    () =>
      selectBuiltinRelations(detail, {
        capability: true,
        loading: resourceMap.isLoading,
        error: resourceMap.error,
        response: resourceMap.data,
      }),
    [detail, resourceMap.data, resourceMap.error, resourceMap.isLoading],
  );
  useEffect(() => {
    if (relations) onRelations({ query, relations });
  }, [onRelations, query, relations]);

  return null;
});

/**
 * Resource Explorer (ADR 0018)
 * --------------------------------------------------------------------------
 * API Group → Kind → Namespace → 항목 순으로 좁혀 보는 **조회 전용** 화면입니다.
 *
 * - 브라우저는 BFF의 catalog/list/detail 세 경로만 부릅니다. Kubernetes 직접 호출은 없습니다.
 * - 목록은 서버 keyset cursor로만 이어봅니다. offset 페이징이 없습니다 (ADR 0003).
 * - 폴링하지 않습니다. 갱신은 사용자가 "다시 조회"로 일으킵니다.
 * - 생성·수정·삭제 경로가 없습니다. 상세도 정제된 읽기 전용 YAML뿐입니다.
 * - 필터·선택은 URL에 남겨 새로고침·공유에 견딥니다.
 */
export function ResourceExplorer() {
  const [params, setParams] = useSearchParams();
  const { clusterId, namespace } = useDashboardParams();
  const scope = useScope();
  const authGeneration = useScopeAuthGeneration();
  const { t } = useT("resources");
  const scopeVerified = Boolean(scope.data && !scope.isFetching && !scope.isError);
  const verifiedScope = scopeVerified ? scope.data : undefined;
  const retainedScopeRef = useRef<ScopeResponse>();
  /* pending/error는 계정 변경의 증거가 아닙니다. 마지막 검증 Scope는 subtree를
     마운트해 두는 용도로만 보존하고, 아래 fence가 표시·입력·설정 접근을 막습니다. */
  if (verifiedScope) retainedScopeRef.current = verifiedScope;
  const renderedScope = verifiedScope ?? retainedScopeRef.current;
  const scopeFenced = !verifiedScope;
  const available = renderedScope?.canExploreResources ?? false;
  const authorizedScopeFingerprint = useMemo(
    () => scopeFingerprint(renderedScope, clusterId),
    [renderedScope, clusterId],
  );
  const preferencePartition = renderedScope?.preferencePartition ?? "";
  /* partition이 없으면 같은 계정임을 증명할 수 없으므로 generation을 임시 경계로만
     씁니다. 검증 partition이 있으면 routine token 회전에서 identity가 안정적입니다. */
  const verifiedAuthorizationKey = preferencePartition
    ? `${preferencePartition.length}:${preferencePartition}|${authorizedScopeFingerprint}`
    : `unverified:${authGeneration}|${authorizedScopeFingerprint}`;
  const clusterAuthorized = available && scopeAllowsNamespace(renderedScope, clusterId, "all");

  const catalog = useResourceCatalog(
    clusterId,
    clusterAuthorized && !scopeFenced,
    verifiedAuthorizationKey,
  );
  const descriptors = catalog.data?.items ?? [];

  const selectedKey = params.get("res") ?? "";
  const namePrefix = params.get("q") ?? "";
  const labelSelector = params.get("labels") ?? "";
  const order = params.get("order") === "desc" ? "desc" : "asc";
  const item = params.get("item") ?? "";

  /* 로컬 입력은 즉시 요청하지 않습니다 — 타이핑마다 서버를 부르면 폴링과 다르지 않습니다.
     "조회" 버튼(또는 Enter)에서만 URL이 바뀌고 그때 요청이 나갑니다. */
  const [nameDraft, setNameDraft] = useState(namePrefix);
  const [labelDraft, setLabelDraft] = useState(labelSelector);

  /* `/`는 이 화면의 이름 입력으로만 포커스를 옮깁니다. 팔레트를 열지 않습니다. */
  const nameInputRef = useRef<HTMLInputElement>(null);
  useSlashFocus(nameInputRef);

  const descriptor: ResourceDescriptor | undefined = useMemo(
    () => descriptors.find((d) => descriptorKey(d) === selectedKey),
    [descriptors, selectedKey],
  );
  const gvr = useMemo(() => parseKey(selectedKey), [selectedKey]);
  const effectiveNamespace = descriptor?.namespaced === false ? "all" : namespace;
  const resourceAuthorized = clusterAuthorized && scopeAllowsNamespace(
    renderedScope,
    clusterId,
    effectiveNamespace,
  );

  const list = useResourceList(
    {
      clusterId,
      group: gvr?.group ?? "",
      version: gvr?.version ?? "",
      resource: gvr?.resource ?? "",
      namespaced: descriptor?.namespaced ?? true,
      namespace: effectiveNamespace,
      namePrefix,
      labelSelector,
      order,
      limit: PAGE_SIZE,
      preferencePartition,
      scopeFingerprint: authorizedScopeFingerprint,
      maxPages: MAX_RETAINED_PAGES,
    },
    /* 카탈로그에서 이 GVR의 namespaced 여부를 알기 전에는 요청하지 않습니다 —
       cluster 범위 리소스에 ns를 붙여 보내면 서버가 400으로 거절합니다. */
    resourceAuthorized && !scopeFenced && Boolean(gvr) && Boolean(descriptor),
  );

  const pages = list.data?.pages ?? EMPTY_PAGES;
  const columns = pages[0]?.columns ?? EMPTY_COLUMNS;
  const retainedRows = useMemo(() => pages.flatMap((page) => page.items.map((item) => ({
    item,
    schemaMatches: (page.columns ?? []).length === columns.length && (page.columns ?? []).every((name, index) => name === columns[index]),
  }))), [pages, columns]);
  const deduped = useMemo(() => dedupeRows(retainedRows), [retainedRows]);
  const rows = deduped.rows;
  const total = pages[0]?.total ?? 0;
  /* printer column은 **서버가 평가한 문자열**입니다(ADR 0065 C2 결정 3).
     브라우저는 전체 객체도 JSONPath도 받지 않습니다. 열 이름은 응답의 순서가
     정본이고, 행의 값은 그 순서와 같은 길이입니다. */
  const schemaDrift = retainedRows.some((row) => !row.schemaMatches);
  const retainedAtCap = pages.length >= MAX_RETAINED_PAGES && Boolean(pages[pages.length - 1]?.nextCursor);
  const preferenceScope = useMemo<PreferenceScope | undefined>(
    () => preferencePartition && clusterId && selectedKey
      ? { partition: preferencePartition, clusterId, gvr: selectedKey }
      : undefined,
    [preferencePartition, clusterId, selectedKey],
  );
  const [activePreferencePartition, setActivePreferencePartition] = useState<string>();
  const [preferenceEpoch, setPreferenceEpoch] = useState(0);
  const [selectedUIDs, setSelectedUIDs] = useState<Set<string>>(() => new Set());
  const fencedPartitionRef = useRef<string>();
  const suspendedPreferencePartitionRef = useRef<string>();
  if (scopeFenced && activePreferencePartition) {
    /* pending 첫 commit부터 저장소를 닫습니다. 같은 partition 성공도 effect가 marker를
       비파괴적으로 다시 확인하기 전에는 설정을 읽거나 쓰지 못합니다. */
    suspendedPreferencePartitionRef.current = activePreferencePartition;
    setActivePreferencePartition(undefined);
  }

  useEffect(() => {
    if (scopeFenced) return undefined;
    if (!resourceAuthorized || !preferencePartition) {
      fencedPartitionRef.current = undefined;
      suspendedPreferencePartitionRef.current = undefined;
      setActivePreferencePartition(undefined);
      return undefined;
    }
    /* 다른 탭이 계정을 바꾼 뒤에는 이전 탭이 stale 인증 상태로 같은 `/scope`를
       다시 받아도 marker를 되찾으면 안 됩니다. */
    if (fencedPartitionRef.current === preferencePartition) {
      setActivePreferencePartition(undefined);
      return undefined;
    }
    fencedPartitionRef.current = undefined;
    const resuming = suspendedPreferencePartitionRef.current === preferencePartition;
    if (!resuming) suspendedPreferencePartitionRef.current = undefined;
    const activated = resuming
      ? resumePreferencePartition(preferencePartition)
      : activatePreferencePartition(preferencePartition);
    if (activated) suspendedPreferencePartitionRef.current = undefined;
    setActivePreferencePartition(activated ? preferencePartition : undefined);
    if (!activated) return undefined;
    return watchPreferencePartition(preferencePartition, () => {
      fencedPartitionRef.current = preferencePartition;
      suspendedPreferencePartitionRef.current = preferencePartition;
      setActivePreferencePartition(undefined);
      setSelectedUIDs(new Set());
      setPreferenceEpoch((value) => value + 1);
    });
  }, [preferencePartition, resourceAuthorized, scope.dataUpdatedAt, scopeFenced]);

  const columnDefinitions = useMemo(() => buildExplorerColumns(
    descriptor?.namespaced !== false,
    columns,
    {
      select: t("resources.list.select"),
      name: effectiveNamespace === "all" ? t("resources.list.namespaceName") : t("resources.list.name"),
      namespace: t("resources.list.namespace"),
      created: t("resources.list.created"),
    },
  ), [columns, descriptor?.namespaced, effectiveNamespace, t]);
  const [columnSettings, setColumnSettings] = useState(() => sanitizeColumnSettings([]));
  const preferenceReady = !scopeFenced && resourceAuthorized && activePreferencePartition === preferencePartition;
  const preferenceReadyRef = useRef(preferenceReady);
  preferenceReadyRef.current = preferenceReady;

  useEffect(() => {
    const stored = preferenceReady && preferenceScope
      ? readTablePreference(preferenceScope)
      : undefined;
    setColumnSettings(sanitizeColumnSettings(columnDefinitions, stored));
  }, [columnDefinitions, preferenceEpoch, preferenceReady, preferenceScope]);

  const persistColumns = useCallback((next: ReturnType<typeof sanitizeColumnSettings>) => {
    setColumnSettings(next);
    if (preferenceReadyRef.current && preferenceScope) writeTablePreference(preferenceScope, next);
  }, [preferenceReady, preferenceScope]);

  const visibleColumnSettings = useMemo(
    () => preferenceReady ? columnSettings : sanitizeColumnSettings(columnDefinitions),
    [columnDefinitions, columnSettings, preferenceReady],
  );
  const settingById = useMemo(() => new Map(visibleColumnSettings.map((setting) => [setting.id, setting])), [visibleColumnSettings]);
  const visibleColumns = useMemo(
    () => columnDefinitions.filter((column) => settingById.get(column.id)?.visible !== false),
    [columnDefinitions, settingById],
  );
  const tableWidth = visibleColumns.reduce((sum, column) => sum + (settingById.get(column.id)?.width ?? column.width), 0);
  const currentSelectionIdentity = selectionIdentity({
    partition: preferencePartition || undefined,
    clusterId,
    gvr: selectedKey,
    scopeFingerprint: authorizedScopeFingerprint,
    namespace: effectiveNamespace,
    namePrefix,
    labelSelector,
    order,
  });
  const resourceAuthorizationIdentity = resourceAuthorized
    ? verifiedAuthorizationKey
    : `scope-unavailable:${authorizedScopeFingerprint}`;
  const identityBoundState = `${resourceAuthorizationIdentity}|${currentSelectionIdentity}`;
  const [selectedIdentity, setSelectedIdentity] = useState(identityBoundState);
  /* 이 갱신은 같은 컴포넌트의 render 중 일어나므로 React가 현재 출력을 버리고
     다시 계산합니다. 새 partition/권한 화면이 commit되기 전에 선택이 비워지고,
     drawer key도 같은 render에서 바뀌어 옛 초안 인스턴스가 먼저 사라집니다. */
  if (selectedIdentity !== identityBoundState) {
    setSelectedIdentity(identityBoundState);
    setSelectedUIDs(new Set());
  }

  useEffect(() => {
    if (!list.isSuccess) return;
    const loaded = new Set(rows.map((row) => row.item.uid));
    setSelectedUIDs((current) => new Set([...current].filter((uid) => loaded.has(uid)).slice(0, MAX_SELECTION)));
  }, [list.dataUpdatedAt, list.isSuccess, rows]);
  /* 전체 객체 인덱스가 metadata 모드로 떨어진 사실은 **보이게** 합니다 —
     열이 조용히 사라지면 운영자는 CRD가 열을 선언하지 않았다고 읽습니다. */
  const columnsDegraded = descriptor?.fullObject === "degraded";
  const kind = list.data?.pages?.[0]?.kind ?? descriptor?.kind ?? "";
  const observedAt = list.data?.pages?.[0]?.observedAt;

  const [selNs, selName, selUid] = item ? item.split("/") : ["", "", ""];
  const target = selName && selUid ? { namespace: selNs, name: selName, uid: selUid } : null;
  const targetAuthorized = Boolean(
    target &&
      resourceAuthorized &&
      (descriptor?.namespaced === false || (
        Boolean(target.namespace) && scopeAllowsNamespace(renderedScope, clusterId, target.namespace)
      )),
  );
  const object = useResourceObject(
    clusterId,
    gvr ?? { group: "", version: "", resource: "" },
    target,
    targetAuthorized && !scopeFenced,
    verifiedAuthorizationKey,
  );

  /**
   * 상세를 **실제로 열어본 것**만 최근 항목에 남깁니다. (ADR 0023)
   *
   * 신원의 근거는 전부 서버 응답입니다 — URL의 `item`은 사용자가 손댈 수 있고,
   * 404·403·UID 불일치로 끝난 조회는 "본 적 없는 리소스"입니다. 실패했거나 아직
   * 확정되지 않은 조회를 남기면 다음에 팔레트를 열었을 때 존재하지 않는 항목이
   * 제목을 달고 나옵니다.
   */
  const detail = object.isSuccess ? object.data : undefined;
  const exactDetail = isExactDetailFocus(detail, clusterId, gvr, target)
    ? detail
    : undefined;
  const exactRef = useMemo<RecentRef | undefined>(() => exactDetail ? ({
    clusterId: exactDetail.clusterId,
    group: exactDetail.group,
    version: exactDetail.version,
    resource: exactDetail.resource,
    namespace: exactDetail.namespace ?? "",
    name: exactDetail.name,
    uid: exactDetail.uid,
  }) : undefined, [exactDetail]);
  const [favorite, setFavorite] = useState(false);
  useEffect(() => {
    if (!preferenceReady || !exactRef) { setFavorite(false); return; }
    const items = readFavorites({ partition: preferencePartition, clusterId });
    setFavorite(items.some((item) => item.group === exactRef.group && item.version === exactRef.version &&
      item.resource === exactRef.resource && item.uid === exactRef.uid));
  }, [clusterId, exactRef, preferenceEpoch, preferencePartition, preferenceReady]);
  const toggleFavorite = useCallback(() => {
    if (!preferenceReadyRef.current || !exactRef) return;
    const scope = { partition: preferencePartition, clusterId };
    const current = readFavorites(scope);
    const matches = (item: RecentRef) => item.group === exactRef.group && item.version === exactRef.version &&
      item.resource === exactRef.resource && item.uid === exactRef.uid;
    const next = current.some(matches)
      ? current.filter((item) => !matches(item))
      : [exactRef, ...current].slice(0, FAVORITES_MAX_ITEMS);
    if (writeFavorites(scope, next)) setFavorite(!current.some(matches));
  }, [clusterId, exactRef, preferencePartition]);
  const relationRequest = useMemo(() => builtinRelationRequest(exactDetail), [exactDetail]);
  const canUseResourceMap = renderedScope?.canUseResourceMap === true;
  const relationFocusKey =
    exactDetail && relationRequest
      ? builtinRelationFocusKey(exactDetail)
      : undefined;
  const relationEligible = Boolean(
    target &&
      available &&
      !scopeFenced &&
      descriptor?.state === "ready" &&
      canUseResourceMap &&
      exactDetail &&
      relationRequest,
  );
  const activeRelationQuery = useMemo<ActiveBuiltinRelationQuery | undefined>(
    () =>
      relationEligible && relationFocusKey && renderedScope
        ? { focusKey: relationFocusKey, scope: renderedScope }
        : undefined,
    [relationEligible, relationFocusKey, renderedScope],
  );
  const [queriedRelations, setQueriedRelations] = useState<BuiltinRelationQueryResult>();
  const localUnsupportedRelations =
    exactDetail && relationRequest && !canUseResourceMap
      ? selectBuiltinRelations(exactDetail, {
          capability: false,
          loading: false,
          error: undefined,
        })
      : undefined;
  const relations = activeRelationQuery
    ? queriedRelations?.query === activeRelationQuery
      ? queriedRelations.relations
      : undefined
    : localUnsupportedRelations;
  useEffect(() => {
    if (exactRef) rememberRecent(exactRef);
  }, [exactRef]);

  const setParam = useCallback(
    (key: string, value: string) =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (value) p.set(key, value);
          else p.delete(key);
          return p;
        },
        { replace: true },
      ),
    [setParams],
  );
  const closeDetail = useCallback(() => setParam("item", ""), [setParam]);
  const selectedAll = rows.length > 0 && rows.every((row) => selectedUIDs.has(row.item.uid));
  const toggleAll = () => {
    if (selectedAll) {
      setSelectedUIDs(new Set());
      return;
    }
    setSelectedUIDs(new Set(rows.slice(0, MAX_SELECTION).map((row) => row.item.uid)));
  };
  const toggleRow = (uid: string) => {
    setSelectedUIDs((current) => {
      const next = new Set(current);
      if (next.has(uid)) next.delete(uid);
      else if (next.size < MAX_SELECTION) next.add(uid);
      return next;
    });
  };
  const updateColumnWidth = (id: string, width: number, persist: boolean) => {
    const next = columnSettings.map((setting) => setting.id === id ? { ...setting, width } : setting);
    if (persist) persistColumns(next);
    else setColumnSettings(next);
  };
  const toggleColumn = (id: string) => {
    const definition = columnDefinitions.find((column) => column.id === id);
    if (!definition || definition.required) return;
    persistColumns(columnSettings.map((setting) => setting.id === id ? { ...setting, visible: !setting.visible } : setting));
  };

  const applyFilters = () => {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (nameDraft) p.set("q", nameDraft);
        else p.delete("q");
        if (labelDraft) p.set("labels", labelDraft);
        else p.delete("labels");
        p.delete("item");
        return p;
      },
      { replace: true },
    );
  };

  const selectResource = (value: string) => {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (value) p.set("res", value);
        else p.delete("res");
        p.delete("item");
        return p;
      },
      { replace: true },
    );
  };

  const openResourceLink = (link: ResourceBuiltinLink) => {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("res", `${link.group}/${link.version}/${link.resource}`);
        p.set("item", `${link.namespace ?? ""}/${link.name}/${link.uid}`);
        return p;
      },
      { replace: true },
    );
  };

  /* 기능 자체가 없는 배포는 필터도 보여주지 않습니다 — 눌러도 되지 않는 컨트롤은 소음입니다. */
  if (!scopeFenced && (!resourceAuthorized || stateFromError(catalog.error) === "unavailable")) {
    return (
      <Panel title="Resource Explorer" subtitle="조회 전용">
        <StateBlock notice={stateNotice("unavailable")} />
      </Panel>
    );
  }

  const catalogMessage = requestErrorMessage(catalog.error);
  const listMessage = requestErrorMessage(list.error);
  const partialListMessage = rows.length > 0 ? listMessage : undefined;
  /* allowlist에서 빠진 GVR로 deep link가 들어오면 빈 표가 아니라 "미등록"을 알립니다. */
  const unknownResource = Boolean(gvr) && Boolean(catalog.data) && !descriptor;
  const state = explorerState(descriptor, rows.length > 0 ? undefined : list.error, rows.length, list.isSuccess);

  return (
    <>
      {scopeFenced && (
        <Panel title="Resource Explorer" subtitle="조회 전용">
          {scope.isError ? <StateBlock notice={stateNotice("unavailable")} /> : <LoadingState />}
        </Panel>
      )}
      <div hidden={scopeFenced} aria-hidden={scopeFenced || undefined} {...(scopeFenced ? { inert: "" } : {})}>
        <Panel title="필터" subtitle="Scope는 서버가 강제합니다 — 여기 값은 요청 힌트입니다">
        <div className="resource-filters">

          <Combobox
            id="resource-kind"
            label="리소스 종류"
            value={selectedKey}
            disabled={catalog.isLoading || descriptors.length === 0}
            options={descriptors.map((d) => ({
              value: descriptorKey(d),
              label: `${d.kind || d.resource} · ${d.group}/${d.version}`,
              note: d.state === "ready" ? undefined : STATE_LABEL[d.state],
            }))}
            onSelect={selectResource}
          />

          <label className="field" htmlFor="resource-name">
            <span className="field__label">이름 prefix</span>
            <input
              id="resource-name"
              ref={nameInputRef}
              type="search"
              value={nameDraft}
              maxLength={MAX_NAME_FILTER}
              spellCheck={false}
              placeholder="payments-"
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyFilters();
              }}
            />
          </label>

          <label className="field field--grow" htmlFor="resource-labels">
            <span className="field__label">Label selector</span>
            <input
              id="resource-labels"
              type="text"
              value={labelDraft}
              maxLength={MAX_LABEL_SELECTOR}
              spellCheck={false}
              placeholder="app=payments,tier!=batch"
              onChange={(e) => setLabelDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyFilters();
              }}
            />
          </label>

          <button type="button" className="ds-button ds-button--sm" onClick={applyFilters}>
            조회
          </button>
        </div>
        {catalogMessage && (
          <p className="resource-filters__error" role="alert">
            {catalogMessage}
          </p>
        )}
        {catalog.data?.degraded && (
          <p className="muted" role="note">
            일부 API group discovery가 실패했습니다{catalog.data.reason ? ` — ${catalog.data.reason}` : ""}. 목록이 완전하지 않을 수 있습니다.
          </p>
        )}
      </Panel>

      <Panel
        title={
          <span className="row">
            <span>{kind || "리소스"}</span>
            {descriptor && descriptor.state !== "ready" && (
              <span className="muted">{STATE_LABEL[descriptor.state]}</span>
            )}
          </span>
        }
        subtitle={
          gvr
            ? `${selectedKey}${total ? ` · 인덱스 ${total}건` : ""}${observedAt ? ` · 관측 ${observedAt}` : ""}`
            : "왼쪽에서 리소스 종류를 고르세요"
        }
        actions={
          gvr ? (
            <button type="button" className="linkish" onClick={() => void list.refetch()} disabled={list.isFetching}>
              다시 조회
            </button>
          ) : undefined
        }
        flush
      >
        {!gvr ? (
          <div className="state">
            <span className="state__glyph" aria-hidden="true">
              →
            </span>
            <span className="state__title">리소스 종류를 선택하세요</span>
            <span className="state__detail">탐색 대상으로 등록된 리소스만 목록에 나옵니다.</span>
          </div>
        ) : unknownResource ? (
          <StateBlock
            notice={stateNotice("missing", "이 리소스는 탐색 대상으로 등록되어 있지 않습니다. 목록에서 다른 종류를 고르세요.")}
          />
        ) : catalog.isLoading || (list.isLoading && rows.length === 0) ? (
          <LoadingState />
        ) : listMessage && rows.length === 0 ? (
          <div className="state state--error" role="alert">
            <span className="state__glyph" aria-hidden="true">
              ✕
            </span>
            <span className="state__title">목록을 불러오지 못했습니다</span>
            <span className="state__detail">{listMessage}</span>
          </div>
        ) : state !== "ready" ? (
          <StateBlock notice={stateNotice(state, descriptor?.reason)} />
        ) : (
          <>
            {columnsDegraded && (
              <div className="state state--warning resource-columns__notice" role="note">
                <span className="state__glyph" aria-hidden="true">
                  ▲
                </span>
                <span className="state__title">{t("resources.deg")}</span>
                <span className="state__detail">
                  {t("resources.deg.d", { reason: descriptor?.fullObjectReason ?? "" })}
                </span>
              </div>
            )}
            {partialListMessage && (
              <p className="resource-list__warning" role="alert">
                {t("resources.list.partialFailure", { reason: partialListMessage })}
              </p>
            )}
            {schemaDrift && <p className="resource-list__warning" role="alert">{t("resources.list.schemaDrift")}</p>}
            {deduped.duplicateCount > 0 && (
              <p className="resource-list__warning" role="alert">
                {t("resources.list.duplicateUid", { count: deduped.duplicateCount })}
              </p>
            )}
            <details className="resource-columns">
              <summary>{t("resources.list.columns")}</summary>
              <div className="resource-columns__choices">
                {columnDefinitions.map((column) => (
                  <label key={column.id}>
                    <input
                      type="checkbox"
                      checked={settingById.get(column.id)?.visible !== false}
                      disabled={column.required}
                      onChange={() => toggleColumn(column.id)}
                    />
                    {column.label}{column.required ? ` · ${t("resources.list.required")}` : ""}
                  </label>
                ))}
              </div>
            </details>
            <VirtualTable
              items={rows}
              rowHeight={TABLE_ROW_HEIGHT}
              height={TABLE_HEIGHT}
              overscan={TABLE_OVERSCAN}
              contentWidth={tableWidth}
              columns={visibleColumns.map((column) => `${settingById.get(column.id)?.width ?? column.width}px`)}
              header={
                <tr>
                  {visibleColumns.map((column) => {
                    const width = settingById.get(column.id)?.width ?? column.width;
                    if (column.kind === "select") {
                      return (
                        <th key={column.id} className="resource-column resource-column--select">
                          <input
                            type="checkbox"
                            aria-label={t("resources.list.selectAll")}
                            checked={selectedAll}
                            onChange={toggleAll}
                          />
                        </th>
                      );
                    }
                    const sortable = column.kind === "name";
                    return (
                      <th key={column.id} className="resource-column" aria-sort={sortable ? (order === "asc" ? "ascending" : "descending") : undefined} aria-disabled={sortable ? undefined : true}>
                        {sortable ? (
                          <button type="button" className="resource-column__sort" onClick={() => setParam("order", order === "asc" ? "desc" : "")}>
                            {column.label}<span aria-hidden="true">{order === "asc" ? " ↑" : " ↓"}</span>
                          </button>
                        ) : (
                          <span>{column.label}<span className="resource-column__unsortable">{t("resources.list.unsortable")}</span></span>
                        )}
                        <ColumnResizeHandle
                          column={column}
                          width={width}
                          label={t("resources.list.resize", { column: column.label })}
                          onWidth={(next) => updateColumnWidth(column.id, next, false)}
                          onCommit={(next) => updateColumnWidth(column.id, next, true)}
                        />
                      </th>
                    );
                  })}
                </tr>
              }
              getKey={(row) => row.item.uid}
              getRowClassName={(row) => item === `${row.item.namespace ?? ""}/${row.item.name}/${row.item.uid}` ? "is-selected" : undefined}
              renderRow={(row) => (
                <>
                  {visibleColumns.map((column) => {
                    const value = row.item;
                    if (column.kind === "select") return (
                      <td key={column.id} className="resource-column--select">
                        <input
                          type="checkbox"
                          aria-label={t("resources.list.selectRow", { name: value.name })}
                          checked={selectedUIDs.has(value.uid)}
                          onChange={() => toggleRow(value.uid)}
                        />
                      </td>
                    );
                    if (column.kind === "name") return (
                      <td key={column.id} className="ds-ident">
                        <button type="button" className="linkish" onClick={() => setParam("item", `${value.namespace ?? ""}/${value.name}/${value.uid}`)}>{value.name}</button>
                      </td>
                    );
                    if (column.kind === "namespace") return <td key={column.id}>{value.namespace ?? "-"}</td>;
                    if (column.kind === "created") return <td key={column.id}>{value.createdAt ?? "-"}</td>;
                    return <td key={column.id} className="ds-ident">{row.schemaMatches ? value.columns?.[column.printerIndex ?? -1] || "-" : "-"}</td>;
                  })}
                </>
              )}
            />
            <div className="resource-list__footer">
              <span className="muted">
                {t("resources.list.shown", { count: rows.length, selected: selectedUIDs.size })}
                {list.hasNextPage ? t("resources.list.next") : t("resources.list.last")}
              </span>
              {retainedAtCap && <strong>{t("resources.list.cap", { count: rows.length })}</strong>}
              {list.hasNextPage && (
                <button
                  type="button"
                  className="ds-button ds-button--sm ds-button--ghost"
                  onClick={() => void list.fetchNextPage()}
                  disabled={list.isFetchingNextPage}
                >
                  {list.isFetchingNextPage ? t("resources.list.loading") : t("resources.list.more")}
                </button>
              )}
            </div>
          </>
        )}
      </Panel>

      {activeRelationQuery && exactDetail && relationRequest && (
        <BuiltinRelationQueryBridge
          key={activeRelationQuery.focusKey}
          detail={exactDetail}
          relationRequest={relationRequest}
          query={activeRelationQuery}
          onRelations={setQueriedRelations}
        />
      )}
      <ResourceDetailDrawer
        key={resourceAuthorizationIdentity}
        open={Boolean(target)}
        loading={object.isLoading}
        detail={exactDetail}
        error={object.error}
        onClose={closeDetail}
        suspended={scopeFenced}
        preferenceScope={preferenceReady ? preferenceScope : undefined}
        favorite={preferenceReady && exactRef ? { active: favorite, disabled: false, onToggle: toggleFavorite } : undefined}
        relations={relations}
        onResourceLink={openResourceLink}
        /* 검토·적용·삭제 진입점은 **서버가 준 capability**로만 결정됩니다. kind·
           verbs·사용자 역할로 추론하지 않습니다. 셋은 각각 별도의 배포 opt-in이라
           하나만 켜진 상태가 정상입니다. (ADR 0019) */
        canReview={descriptor?.dryRun === true}
        canApply={descriptor?.apply === true}
        canDelete={descriptor?.delete === true}
        /* 적용·삭제 뒤에는 목록과 열려 있는 상세를 **데이터만** 다시 불러옵니다.
           페이지를 다시 마운트하지 않으므로 필터·스크롤은 그대로입니다. */
        onChanged={() => {
          void list.refetch();
          void object.refetch();
        }}
      />
      </div>
    </>
  );
}
